// ─────────────────────────────────────────────────────────────────────────────
// PROTOTYPE — front-end-only node catalog for the automation flow canvas.
//
// Single source of truth for the sidebar sections (Triggers / Data / Actions),
// which bricks exist, and the fields each trigger emits (so action bricks can map
// from them). STUB DATA — nothing talks to the server yet.
//
// Names are copied verbatim from the CURRENT automation UI so terminology matches:
//   - Trigger labels  ← AutomationWizard.js EVENT_OPTIONS / AutomationsPage EVENT_LABEL
//   - Action labels   ← ActionTypePicker.js ACTION_TYPES (incl. their "available" flag;
//                        EMAIL/SMS/WHATSAPP are marked "בקרוב" there → available:false)
// ─────────────────────────────────────────────────────────────────────────────

// ── Trigger output fields (feed the action "map from" dropdowns) ─────────────
const ARBOX_NEW_CLIENT_FIELDS = [
    {path: "arbox.user_id", label: "מזהה לקוח Arbox", sample: 250999},
    {path: "arbox.first_name", label: "שם פרטי", sample: "דנה"},
    {path: "arbox.last_name", label: "שם משפחה", sample: "כהן"},
    {path: "arbox.email", label: "אימייל", sample: "dana@example.com"},
    {path: "arbox.phone", label: "טלפון", sample: "0547977234"},
    {path: "arbox.gender", label: "מגדר", sample: "female"},
    {path: "arbox.created_time", label: "נוצר בתאריך", sample: "2026-07-14 09:12"},
    {path: "arbox.user_role", label: "תפקיד", sample: "client"},
];

// Arbox status-change fields (enabled/disabled/frozen/expiration/deleted) — carries the changed client's
// state. Mirrors the server ArboxStatusOutputs.FIELDS. `email` is the usual match key for the sync bricks.
const ARBOX_STATUS_FIELDS = [
    {path: "arbox.user_id", label: "מזהה לקוח Arbox", sample: 250999},
    {path: "arbox.email", label: "אימייל", sample: "dana@example.com"},
    {path: "arbox.first_name", label: "שם פרטי", sample: "דנה"},
    {path: "arbox.last_name", label: "שם משפחה", sample: "כהן"},
    {path: "arbox.phone", label: "טלפון", sample: "0547977234"},
    {path: "arbox.active", label: "פעיל (1/0)", sample: 0},
    {path: "arbox.membership_end_date", label: "תאריך סיום מנוי", sample: "2026-12-31"},
];

// Internal-event triggers — the full trainee field set (mirrors the public-api PublicTraineeData
// shape), exposed under the `trainee.*` namespace so mappings read {{trainee.name}}.
const USER_EVENT_FIELDS = [
    {path: "trainee.id", label: "מזהה מתאמן", sample: 8123},
    {path: "trainee.userId", label: "מזהה משתמש", sample: 45011},
    {path: "trainee.name", label: "שם", sample: "ישראל ישראלי"},
    {path: "trainee.email", label: "אימייל", sample: "user@example.com"},
    {path: "trainee.phoneNumber", label: "טלפון", sample: "+972501234567"},
    {path: "trainee.photoUrl", label: "תמונה", sample: "https://…/photo.jpg"},
    {path: "trainee.gender", label: "מגדר", sample: "MALE"},
    {path: "trainee.lang", label: "שפה", sample: "HE"},
    {path: "trainee.username", label: "שם משתמש", sample: "user@example.com"},
    {path: "trainee.dateOfBirth", label: "תאריך לידה", sample: "1990-05-14"},
    {path: "trainee.enabled", label: "פעיל", sample: true},
    {path: "trainee.address", label: "כתובת", sample: "תל אביב"},
    {path: "trainee.companyId", label: "מזהה חברה", sample: 42},
    {path: "trainee.createdByType", label: "נוצר על ידי", sample: "ADMIN"},
    {path: "trainee.createdByIdentifier", label: "מזהה יוצר", sample: "—"},
    {path: "trainee.forcePasswordChange", label: "חייב החלפת סיסמה", sample: false},
    {path: "trainee.expirationDate", label: "תאריך תפוגה", sample: "2026-12-31"},
    {path: "trainee.trainerId", label: "מזהה מאמן", sample: 77},
    {path: "trainee.height", label: "גובה (ס״מ)", sample: 178},
    {path: "trainee.weightUnit", label: "יחידת משקל", sample: "KG"},
    {path: "trainee.experienceLevel", label: "רמת ניסיון", sample: "INTERMEDIATE"},
    {path: "trainee.equipmentType", label: "סוג ציוד", sample: "FULL_GYM"},
    {path: "trainee.numOfDaysTraining", label: "ימי אימון בשבוע", sample: 4},
    {path: "trainee.lastTrained", label: "אימון אחרון", sample: "2026-07-15"},
];

// Domain events carry the trainee block above PLUS their own namespace. The server
// (AutomationV2Dispatcher) normalizes every payload so `trainee.*` resolves on all of them —
// these lists are the extra, event-specific fields layered on top.
const WORKOUT_EVENT_FIELDS = [
    ...USER_EVENT_FIELDS,
    {path: "workout.workoutId", label: "מזהה אימון", sample: 90211},
    {path: "workout.workoutName", label: "שם האימון", sample: "אימון A"},
    {path: "workout.programId", label: "מזהה תוכנית", sample: 5512},
    {path: "workout.programName", label: "שם התוכנית", sample: "היפרטרופיה"},
    {path: "workout.blockIndex", label: "מספר בלוק", sample: 1},
    {path: "workout.weekIndex", label: "מספר שבוע", sample: 3},
    {path: "workout.workoutIndex", label: "מספר אימון", sample: 2},
    {path: "workout.weekType", label: "סוג שבוע", sample: "LOAD"},
    {path: "workout.startedAt", label: "זמן התחלה", sample: 1753500000},
    {path: "workout.completedAt", label: "זמן סיום", sample: 1753504200},
    {path: "workout.durationSeconds", label: "משך (שניות)", sample: 4200},
    {path: "workout.totalExercises", label: "מספר תרגילים", sample: 6},
    {path: "workout.totalSets", label: "מספר סטים", sample: 21},
    {path: "workout.totalVolume", label: "נפח כולל", sample: 12450.5},
];

const WEIGHT_EVENT_FIELDS = [
    ...USER_EVENT_FIELDS,
    {path: "weight.weight", label: "משקל", sample: 78.4},
    {path: "weight.weightUnit", label: "יחידת משקל", sample: "KG"},
    {path: "weight.date", label: "תאריך", sample: 1753488000},
    {path: "weight.source", label: "מקור", sample: "USER_MANUAL"},
];

// FORM_COMPLETED carries only trainee id + name (the payload has no full user block),
// so only those two trainee fields resolve at runtime.
const FORM_EVENT_FIELDS = [
    {path: "trainee.id", label: "מזהה מתאמן", sample: 8123},
    {path: "trainee.name", label: "שם", sample: "ישראל ישראלי"},
    {path: "form.formId", label: "מזהה טופס", sample: 3301},
    {path: "form.formTemplateId", label: "מזהה תבנית טופס", sample: 12},
    {path: "form.formName", label: "שם הטופס", sample: "הצהרת בריאות"},
    {path: "form.date", label: "תאריך מילוי", sample: "2026-07-26T14:03:00Z"},
];

// Fireberry (CRM) fields — placeholder shape for the prototype.
const FIREBERRY_CONTACT_FIELDS = [
    {path: "fireberry.contactId", label: "מזהה איש קשר", sample: "fb_88213"},
    {path: "fireberry.name", label: "שם", sample: "יוסי לוי"},
    {path: "fireberry.email", label: "אימייל", sample: "yossi@example.com"},
    {path: "fireberry.phone", label: "טלפון", sample: "0521234567"},
];

// Incoming-webhook trigger fields — the posted JSON body (dynamic; a few common
// keys shown so mapping has something to pick).
const WEBHOOK_FIELDS = [
    {path: "webhook.body", label: "כל גוף הבקשה (JSON)", sample: "{...}"},
    {path: "webhook.email", label: "email", sample: "person@example.com"},
    {path: "webhook.name", label: "name", sample: "ישראל ישראלי"},
    {path: "webhook.phone", label: "phone", sample: "0501234567"},
];

// ── SYSTEMS ──────────────────────────────────────────────────────────────────
// The trigger picker is two-step: choose a system, then a trigger it exposes.
// iconType: "arbox"/"fireberry" render the logo image; "progress" uses a glyph.
export const SYSTEMS = [
    {id: "PROGRESS", label: "Progress", iconType: "progress", available: true,
        description: "אירועים מתוך המערכת"},
    {id: "ARBOX", label: "Arbox", iconType: "arbox", available: true, needsApiKey: true,
        description: "חיבור למערכת Arbox"},
    {id: "FIREBERRY", label: "Fireberry", iconType: "fireberry", available: true, needsApiKey: true,
        description: "חיבור ל-CRM Fireberry"},
    {id: "WEBHOOK", label: "Webhook נכנס", iconType: "webhook", available: true,
        description: "כתובת URL שמערכת חיצונית שולחת אליה"},
];

// ── TRIGGERS ─────────────────────────────────────────────────────────────────
// Each trigger is tagged with `system` (matches a SYSTEMS id). Internal-event
// labels are copied from the current wizard (EVENT_OPTIONS).
export const TRIGGER_CATALOG = [
    {
        kind: "ARBOX_NEW_CLIENT", system: "ARBOX", source: "ARBOX",
        label: "לקוח חדש",
        description: "מופעל כשלקוח חדש נוסף ל-Arbox",
        needsApiKey: true, available: true,
        outputs: ARBOX_NEW_CLIENT_FIELDS,
    },
    {
        kind: "ARBOX_CLIENT_STATUS", system: "ARBOX", source: "ARBOX",
        label: "סטטוס לקוח עודכן",
        description: "מופעל כשלקוח הופעל או הושבת ב-Arbox",
        needsApiKey: true, available: true,
        outputs: ARBOX_STATUS_FIELDS,
    },
    {
        kind: "ARBOX_CLIENT_DISABLED", system: "ARBOX", source: "ARBOX",
        label: "לקוח הושבת",
        description: "מופעל כשמנוי/לקוח מושבת ב-Arbox",
        needsApiKey: true, available: true,
        outputs: ARBOX_STATUS_FIELDS,
    },
    {
        kind: "ARBOX_CLIENT_ENABLED", system: "ARBOX", source: "ARBOX",
        label: "לקוח הופעל",
        description: "מופעל כשלקוח מופעל מחדש ב-Arbox",
        needsApiKey: true, available: true,
        outputs: ARBOX_STATUS_FIELDS,
    },
    {
        kind: "ARBOX_CLIENT_EXPIRATION", system: "ARBOX", source: "ARBOX",
        label: "תוקף מנוי עודכן",
        description: "מופעל כשתאריך סיום המנוי של לקוח משתנה ב-Arbox",
        needsApiKey: true, available: true,
        outputs: ARBOX_STATUS_FIELDS,
    },
    {
        kind: "ARBOX_CLIENT_DELETED", system: "ARBOX", source: "ARBOX",
        label: "לקוח נמחק",
        description: "מופעל כשלקוח נמחק מ-Arbox",
        needsApiKey: true, available: true,
        outputs: ARBOX_STATUS_FIELDS,
    },
    // ── Progress (internal events) ──
    {
        kind: "INTERNAL_EVENT", system: "PROGRESS", source: "PROGRESS", eventType: "USER_CREATED",
        label: "משתמש נוצר", description: "user.created",
        needsApiKey: false, available: true, outputs: USER_EVENT_FIELDS,
    },
    {
        kind: "INTERNAL_EVENT", system: "PROGRESS", source: "PROGRESS", eventType: "USER_UPDATED",
        label: "משתמש עודכן", description: "user.updated",
        needsApiKey: false, available: true, outputs: USER_EVENT_FIELDS,
    },
    {
        kind: "INTERNAL_EVENT", system: "PROGRESS", source: "PROGRESS", eventType: "USER_DELETED",
        label: "משתמש נמחק", description: "user.deleted",
        needsApiKey: false, available: true, outputs: USER_EVENT_FIELDS,
    },
    {
        kind: "INTERNAL_EVENT", system: "PROGRESS", source: "PROGRESS", eventType: "WORKOUT_COMPLETED",
        label: "אימון הושלם", description: "workout.completed",
        needsApiKey: false, available: true, outputs: WORKOUT_EVENT_FIELDS,
    },
    {
        kind: "INTERNAL_EVENT", system: "PROGRESS", source: "PROGRESS", eventType: "WEIGHT_UPDATED",
        label: "משקל עודכן", description: "weight.updated",
        needsApiKey: false, available: true, outputs: WEIGHT_EVENT_FIELDS,
    },
    {
        kind: "INTERNAL_EVENT", system: "PROGRESS", source: "PROGRESS", eventType: "FORM_COMPLETED",
        label: "טופס מולא", description: "form.completed",
        needsApiKey: false, available: true, outputs: FORM_EVENT_FIELDS,
    },
    // ── Fireberry (CRM) ──
    {
        kind: "FIREBERRY_NEW_CONTACT", system: "FIREBERRY", source: "FIREBERRY",
        label: "איש קשר חדש",
        description: "מופעל כשנוצר איש קשר חדש ב-Fireberry",
        needsApiKey: true, available: true,
        outputs: FIREBERRY_CONTACT_FIELDS,
    },
    {
        kind: "FIREBERRY_CONTACT_UPDATED", system: "FIREBERRY", source: "FIREBERRY",
        label: "איש קשר עודכן",
        description: "מופעל כשאיש קשר עודכן ב-Fireberry",
        needsApiKey: true, available: true,
        outputs: FIREBERRY_CONTACT_FIELDS,
    },
    // ── Incoming webhook (universal) ──
    {
        kind: "WEBHOOK_RECEIVED", system: "WEBHOOK", source: "WEBHOOK",
        label: "התקבלה בקשה",
        description: "מופעל כשמתקבלת בקשת POST לכתובת ה-Webhook",
        needsApiKey: false, available: true,
        outputs: WEBHOOK_FIELDS,
    },
];

// Triggers available for a given system id.
//
// `availableKinds` (optional Set<string>, from the server /bricks catalog) filters the
// static list to only the triggers the server can actually run. Pass null/undefined to
// show everything (fallback when the catalog hasn't loaded). INTERNAL_EVENT is a single
// server kind that expands into several UI rows (one per eventType), so it's kept whenever
// INTERNAL_EVENT is available.
export const triggersForSystem = (systemId, availableKinds = null) =>
    TRIGGER_CATALOG.filter(t =>
        t.system === systemId &&
        (availableKinds == null || availableKinds.has(t.kind)));

// Systems that have at least one available trigger. `availableSystems` (Set<string> or null).
export const availableSystemList = (availableSystems = null) =>
    SYSTEMS.filter(s => availableSystems == null || availableSystems.has(s.id));

// ── DATA manipulation — an Excel-like FORMULA brick ──────────────────────────
// One "עיבוד נתונים" brick. Instead of fixed operations, the user writes a
// free-text FORMULA referencing any upstream field with {{...}} (like Excel
// cells) and calling functions. They NAME the single output field themselves, so
// downstream bricks map from {{data_<id>.<outputName>}}.
//
// Config lives in node.data.formula = { outputName, expression }.
// Rendered specially in the panel (see NodeConfigPanel → formula flag).

// Expression language: use OPERATORS directly on fields + string/number literals
//   {{arbox.first_name}} + " " + {{arbox.last_name}}   (concat)
//   {{a}} + {{b}}                                       (math)
//   "+972" + {{arbox.phone}}
// Functions are ONLY for what operators can't express (dates, phone, conditions…).
export const FORMULA_OPERATORS = [
    {op: "+", help: "חיבור / שרשור טקסט"},
    {op: "-", help: "חיסור"},
    {op: "*", help: "הכפלה"},
    {op: "/", help: "חלוקה"},
];

export const FORMULA_FUNCTIONS = [
    {name: "FORMATDATE", sig: 'FORMATDATE({{value}}, "yyyy-MM-dd HH:mm", "dd-MM-yyyy")', help: "המר פורמט תאריך"},
    {name: "PHONE", sig: 'PHONE({{value}}, "+972")', help: "נרמל טלפון לפורמט בינלאומי"},
    {name: "UPPER", sig: "UPPER({{text}})", help: "אותיות גדולות"},
    {name: "LOWER", sig: "LOWER({{text}})", help: "אותיות קטנות"},
    {name: "TRIM", sig: "TRIM({{text}})", help: "הסר רווחים מהקצוות"},
    {name: "DEFAULT", sig: 'DEFAULT({{value}}, "ברירת מחדל")', help: "ערך חלופי אם ריק"},
    {name: "ROUND", sig: "ROUND({{number}}, 2)", help: "עיגול"},
    {name: "REPLACE", sig: 'REPLACE({{text}}, "a", "b")', help: "החלף טקסט"},
    {name: "IF", sig: 'IF({{cond}}, "כן", "לא")', help: "תנאי"},
];

// Comparison operators for the Filter brick's structured condition builder.
// `unary` operators (is empty / not empty) don't need a value.
export const FILTER_OPERATORS = [
    {value: "EQ", label: "שווה ל"},
    {value: "NEQ", label: "לא שווה ל"},
    {value: "CONTAINS", label: "מכיל"},
    {value: "NOT_CONTAINS", label: "לא מכיל"},
    {value: "EMPTY", label: "ריק", unary: true},
    {value: "NOT_EMPTY", label: "לא ריק", unary: true},
    {value: "GT", label: "גדול מ"},
    {value: "LT", label: "קטן מ"},
];

export const DATA_CATALOG = [
    {
        kind: "DATA_TRANSFORM", label: "עיבוד נתונים",
        description: "כתוב נוסחה חופשית (כמו אקסל) על שדות מהזרימה, ותן שם לתוצאה",
        available: true,
        formula: true, // → panel renders the formula editor (output name + expression)
        // The output is dynamic — named by the user (data.formula.outputName).
        // A default output descriptor is used until the user names it.
        outputs: [{key: "result", label: "תוצאת הנוסחה", sample: "דנה כהן"}],
    },
    {
        kind: "FILTER", label: "תנאי / סינון",
        description: "המשך רק אם התנאי מתקיים — אחרת עצור את הענף",
        available: true,
        filter: true, // → panel renders the condition builder (structured + advanced)
        // Config lives in node.data.condition = { mode: "STRUCTURED"|"ADVANCED",
        //   field, operator, value } | { expression }.
        // No outputs — a filter only gates flow; it passes the same data through.
        outputs: [],
    },
];

// ── ACTION bricks ────────────────────────────────────────────────────────────
// Every brick has a typed interface:
//   inputs  (aka targets) — fields it REQUIRES, mapped from upstream outputs
//   outputs               — fields it PRODUCES, available to downstream bricks
// Data flows forward along edges: a brick can map from the outputs of ANY brick
// upstream of it (its ancestors), not just the trigger.

// CREATE Trainee inputs — name + email required to make an account.
// Mirrors CreateTraineeBrick.inputs() server-side. Password/username/roles are always generated
// server-side (system-context create) — never mapped here. externalId is the Arbox user_id, stamped
// for dedup/matching (map {{arbox.user_id}} to it).
const CREATE_TRAINEE_TARGETS = [
    {key: "name", label: "שם", required: true},
    {key: "email", label: "אימייל", required: true},
    {key: "phoneNumber", label: "טלפון", required: false},
    {key: "gender", label: "מגדר", required: false},
    {key: "dateOfBirth", label: "תאריך לידה", required: false},
    {key: "trainerId", label: "מאמן משויך", required: false},
    {key: "expirationDate", label: "תאריך תפוגה", required: false},
    {key: "password", label: "סיסמה", required: false, passwordMode: true},
    {key: "externalId", label: "מזהה חיצוני (Arbox)", required: false},
];

// UPDATE Trainee inputs — ONE way to find the trainee + which fields to change.
// The `matchField` option (email / trainee id) says HOW to locate it; `matchValue` is the single bound
// value (e.g. {{arbox.email}} from an Arbox trigger, or {{createTrainee.traineeId}} to chain after a
// create). `enabled` sets active state (true/false) — this is how the Arbox disable/enable sync works.
// If no trainee matches in the company, the execution FAILS (visible in the run history).
const UPDATE_TRAINEE_TARGETS = [
    // `matchMode` → the panel renders this FIRST as the "find the trainee" block: a by-email/by-id
    // selector (writes the matchField option) + the value below (static OR dynamic from a previous node).
    {key: "matchValue", label: "בחר מתאמן", required: true, matchMode: true},
    {key: "name", label: "שם", required: false},
    {key: "email", label: "אימייל", required: false},
    {key: "phoneNumber", label: "טלפון", required: false},
    {key: "expirationDate", label: "תאריך תפוגה", required: false},
    {key: "enabled", label: "פעיל (true/false)", required: false},
];

// The "find by" choices a matchMode target offers (mirrors the server matchField option).
export const MATCH_FIELD_CHOICES = [
    {value: "EMAIL", label: "לפי אימייל"},
    {value: "TRAINEE_ID", label: "לפי מזהה מתאמן"},
];

// What Create/Update Trainee PRODUCE — notably traineeId, usable downstream
// (e.g. Send Email → recipient = the newly created trainee).
const UPDATE_TRAINEE_OUTPUTS = [
    {key: "traineeId", label: "מזהה המתאמן", sample: 8123},
    {key: "userId", label: "מזהה המשתמש", sample: 45011},
    {key: "name", label: "שם", sample: "דנה כהן"},
    {key: "email", label: "אימייל", sample: "dana@example.com"},
    {key: "phoneNumber", label: "טלפון", sample: "+972547977234"},
];

// Create Trainee ALSO produces the generated login credentials, so a downstream
// welcome-email can send the trainee their username + password.
// NOTE (server phase): emailing a raw password is a security consideration —
// prefer a set-password link. Kept here to demonstrate the prototype flow.
const CREATE_TRAINEE_OUTPUTS = [
    ...UPDATE_TRAINEE_OUTPUTS,
    {key: "username", label: "שם משתמש (לכניסה)", sample: "dana@example.com"},
    {key: "password", label: "סיסמה שנוצרה", sample: "Xy7kQp2r"},
];

// Outbound labels/descriptions/availability copied from ActionTypePicker.ACTION_TYPES.
export const ACTION_CATALOG = [
    // Inbound (do something in Progress) — new bricks.
    {
        kind: "CREATE_TRAINEE", label: "צור מתאמן",
        description: "יוצר מתאמן חדש מהנתונים של הטריגר",
        direction: "INBOUND", available: true,
        targets: CREATE_TRAINEE_TARGETS,
        outputs: CREATE_TRAINEE_OUTPUTS,
        // OPTIONS — behavior settings (not data). Turn the built-in welcome email
        // off to send your own via a downstream Email brick.
        options: [
            {key: "sendWelcomeEmail", type: "boolean", label: "שלח אימייל ברוכים הבאים",
             help: "כבה אם תרצה לשלוח אימייל מותאם בשלב נפרד", default: true},
            {key: "forcePasswordChange", type: "boolean", label: "חייב החלפת סיסמה בכניסה הראשונה",
             default: true},
        ],
    },
    {
        kind: "UPDATE_TRAINEE", label: "עדכן מתאמן",
        description: "מעדכן מתאמן קיים — מצא אותו לפי אימייל/מזהה, ועדכן שדות או הפעל/השבת",
        direction: "INBOUND", available: true,
        targets: UPDATE_TRAINEE_TARGETS,
        outputs: UPDATE_TRAINEE_OUTPUTS,
    },
    {
        kind: "DELETE_TRAINEE", label: "מחק מתאמן",
        description: "מוחק מתאמן — מצא אותו לפי אימייל/מזהה; פרטיו נשמרים לשלב הבא (למשל אימייל פרידה)",
        direction: "INBOUND", available: true,
        targets: [
            {key: "matchValue", label: "בחר מתאמן", required: true, matchMode: true},
        ],
        // Outputs are a SNAPSHOT of the deleted trainee, captured before deletion,
        // so a downstream step (e.g. farewell email) can still use email/name.
        outputs: [
            {key: "traineeId", label: "מזהה המתאמן שנמחק", sample: 8123},
            {key: "email", label: "אימייל", sample: "dana@example.com"},
            {key: "name", label: "שם", sample: "דנה כהן"},
            {key: "phoneNumber", label: "טלפון", sample: "+972547977234"},
        ],
    },
    // Onboarding assign bricks — pick a company template/menu/form by NAME (picker dropdown), and
    // the trainee usually comes from an upstream Create-Trainee ({{create_trainee.traineeId}}).
    {
        kind: "ASSIGN_PROGRAM_TEMPLATE", label: "שייך תוכנית אימון",
        description: "משייך תבנית תוכנית אימון למתאמן",
        direction: "INBOUND", available: true,
        targets: [
            {key: "traineeId", label: "מתאמן", required: true, isTarget: true},
            {key: "templateId", label: "תוכנית אימון", required: true, picker: "program"},
        ],
        outputs: [
            {key: "traineeId", label: "מזהה המתאמן", sample: 8123},
            {key: "templateId", label: "מזהה התוכנית", sample: 42},
        ],
        options: [
            {key: "includeRmWeek", type: "boolean", label: "כלול שבוע RM", default: false},
        ],
    },
    {
        kind: "ASSIGN_NUTRITION_MENU", label: "שייך תפריט תזונה",
        description: "משייך תפריט תזונה למתאמן (כל ימות השבוע)",
        direction: "INBOUND", available: true,
        targets: [
            {key: "traineeId", label: "מתאמן", required: true, isTarget: true},
            {key: "menuTemplateId", label: "תפריט תזונה", required: true, picker: "nutrition"},
        ],
        outputs: [
            {key: "traineeId", label: "מזהה המתאמן", sample: 8123},
            {key: "menuId", label: "מזהה התפריט", sample: 7001},
        ],
    },
    {
        kind: "SEND_FORM", label: "שלח טופס",
        description: "שולח טופס למתאמן (למשל הצהרת בריאות)",
        direction: "INBOUND", available: true,
        targets: [
            {key: "traineeId", label: "מתאמן", required: true, isTarget: true},
            {key: "formTemplateId", label: "טופס", required: true, picker: "form"},
        ],
        outputs: [
            {key: "traineeId", label: "מזהה המתאמן", sample: 8123},
            {key: "formId", label: "מזהה הטופס", sample: 3001},
        ],
        options: [
            {key: "required", type: "boolean", label: "טופס חובה", default: false},
        ],
    },
    // Outbound (notify) — names/availability from the current UI.
    {
        kind: "HTTP", label: "HTTP / Webhook",
        description: "הפעל webhook לכל URL",
        direction: "OUTBOUND", available: true,
        targets: [
            {key: "url", label: "כתובת URL", required: true},
            // Free-form request body — write JSON and embed any upstream field with {{...}}
            // (e.g. {"name": "{{trainee.name}}"}). The value is a STATIC template, interpolated
            // at run time, so you can compose it from the trigger + any earlier brick's outputs.
            {key: "body", label: "גוף הבקשה", required: false},
        ],
        // Behavior options (method); headers can be added later.
        // choices are {value,label} objects — the config panel's SELECT renders c.value / c.label.
        options: [
            {key: "method", type: "select", label: "שיטה", default: "POST",
             choices: [
                 {value: "POST", label: "POST"},
                 {value: "GET", label: "GET"},
                 {value: "PUT", label: "PUT"},
                 {value: "PATCH", label: "PATCH"},
                 {value: "DELETE", label: "DELETE"},
             ]},
        ],
        outputs: [
            {key: "statusCode", label: "קוד תגובה", sample: 200},
            {key: "responseBody", label: "גוף התגובה", sample: "{...}"},
        ],
    },
    {
        kind: "EMAIL", label: "אימייל",
        description: "שלח אימייל מותאם עם תבנית טקסט עשיר",
        direction: "OUTBOUND", available: true,
        targets: [
            {key: "to", label: "נמען", required: true},
            {key: "subject", label: "נושא", required: true},
            // richText → the config panel renders the rich-text template editor
            // (with an insert-{{field}} button) instead of a plain input.
            {key: "body", label: "תוכן ההודעה", required: true, richText: true},
        ],
        outputs: [{key: "messageId", label: "מזהה הודעה", sample: "msg_1a2b"}, {key: "sentAt", label: "נשלח בתאריך", sample: "2026-07-14 09:20"}],
    },
    {
        kind: "SMS", label: "SMS",
        description: "שלח הודעת טקסט",
        direction: "OUTBOUND", available: false,
        targets: [{key: "to", label: "נמען", required: true}, {key: "body", label: "תוכן"}],
        outputs: [{key: "messageId", label: "מזהה הודעה", sample: "sms_9f8e"}],
    },
    {
        kind: "PUSH", label: "התראת Push",
        description: "שלח התראה לאפליקציה",
        direction: "OUTBOUND", available: true,
        targets: [
            {key: "traineeId", label: "מתאמן", required: true, isTarget: true},
            {key: "title", label: "כותרת", required: true},
            {key: "body", label: "תוכן", required: true},
        ],
        outputs: [
            {key: "traineeId", label: "מתאמן", sample: 8123},
            {key: "sentAt", label: "נשלח בתאריך", sample: "2026-07-17 09:20"},
        ],
    },
    {
        kind: "WHATSAPP", label: "WhatsApp",
        description: "שלח הודעת WhatsApp",
        direction: "OUTBOUND", available: false,
        targets: [{key: "to", label: "נמען", required: true}, {key: "body", label: "תוכן"}],
        outputs: [{key: "messageId", label: "מזהה הודעה", sample: "wa_3c4d"}],
    },
];

// ── Sidebar sections (the palette renders these in order) ─────────────────────
// Triggers are NOT in the palette: the flow has one static trigger brick on the
// canvas; the user clicks it to choose the trigger. The palette is actions + data.
export const PALETTE_SECTIONS = [
    {id: "DATA", label: "עיבוד נתונים", items: DATA_CATALOG, nodeType: "data"},
    {id: "ACTIONS", label: "פעולות", items: ACTION_CATALOG, nodeType: "action"},
];

export const triggerByKind = (kind, eventType) =>
    TRIGGER_CATALOG.find(t => t.kind === kind && (kind !== "INTERNAL_EVENT" || t.eventType === eventType));

export const actionByKind = (kind) => ACTION_CATALOG.find(a => a.kind === kind);
